import React from 'react';
import { FormField } from 'shared/DEPRECATED/components/form/layout';
import wrappedForm from '@grnhse/react-forms/lib/wrapped_form';
import update from 'immutability-helper';
import { standardRequestWithFlash } from 'shared/utils/request';
import {
  canAssumeMobileDevice,
  canAssumeTouchDevice,
} from 'shared/utils/touch_detection';
import styled from 'styled-components';
import { colors, fontWeight } from '@grnhse/seedling/lib/azalea/constants';
import Button from '@grnhse/seedling/lib/azalea/components/button';
import classNames from 'classnames';
import AnswerInputFactory from './answer_input_factory';
import AnswerWrapper from './answer_wrapper';

const formConfig = {
  initialValues: (props) => {
    const answers = props.questions.map((question) => {
      return {
        value_type: question.value_type,
        question: question,
        question_name: question.name,
        question_options: question.custom_field_options,
        question_id: question.id,
        value: '',
        required: question.required.toString(),
        htmlId: `question_${question.id}`,
      };
    });

    return {
      answers: answers,
    };
  },
  validationRules: () => {
    return {
      'answers.*.value': 'required_if:answers.*.required,true',
    };
  },
  validationErrorMessages: {
    'required_if.answers.*.value': 'This field is required.',
  },
};

class Form extends React.Component {
  static defaultProps = {
    disableSubmit: false,
    paths: {},
  };

  constructor(props) {
    super(props);

    this.componentFactory = new AnswerInputFactory({
      updateAnswerValue: this.updateAnswerValue,
      signedUrl: props.paths.signed_url,
      allowlistedExtensions: props.allowlisted_extensions,
      disableSubmit: props.disableSubmit,
      setUploadError: (error) => this.setState({ error }),
      touchFriendly: canAssumeTouchDevice() || canAssumeMobileDevice(),
    });
  }

  updateAnswerValue = (answer, newValue, processValue) => {
    processValue = processValue || (() => newValue);
    const existingAnswers = this.props.formFields.answers;
    const index = existingAnswers
      .map((a) => a.question_id)
      .indexOf(answer.question_id);

    const processedValue = processValue(existingAnswers[index].value, newValue);

    const answers = update(existingAnswers, {
      [index]: { value: { $set: processedValue } },
    });
    this.props.updateValue('answers', 'passthrough')(answers);
  };

  onSubmit = () => {
    if (this.props.disableSubmit) {
      return;
    }

    const readyToSubmit = confirm(
      'Answers cannot be changed once submitted. Are you ready to submit?'
    );
    if (!readyToSubmit) {
      return;
    }

    standardRequestWithFlash
      .put(this.props.paths.applicationForm, this.props.formPayload())
      .then(() => {
        window.location.replace(this.props.paths.confirmation);
      });
  };

  render() {
    const { name } = this.props;
    const { answers } = this.props.formFields;

    return (
      <StyledForm onSubmit={this.props.wrapSubmit(this.onSubmit)}>
        <FormName>{name}</FormName>

        {answers.map((answer, index) => (
          <AnswerWrapper
            answer={answer}
            key={answer.question_id}
            error={this.props.formErrors[`answers.${index}.value`]}
          >
            <FormField
              data-test="question"
              data-test-name={answer.question_name}
              error={this.props.formErrors[`answers.${index}.value`]}
            >
              {this.componentFactory.componentFor(answer)}
            </FormField>
          </AnswerWrapper>
        ))}

        <SubmitButtonContainer>
          <Button
            useButtonTag
            fancy
            id="submit-button"
            className={classNames({
              'not-clickable': this.props.disableSubmit,
            })}
            type="submit"
            size="large"
          >
            Submit
          </Button>
        </SubmitButtonContainer>
      </StyledForm>
    );
  }
}

export default wrappedForm(formConfig)(Form);

const StyledForm = styled.form`
  padding: 4% 3%;
  background-color: ${colors.white};
  border: 1px solid ${colors.lightGrey};

  #submit-button {
    background-color: ${colors.mediumBlue};
    border-color: ${colors.mediumBlue};

    &.not-clickable {
      pointer-events: none;
    }
  }
`;

const FormName = styled.h2`
  font-size: 22px;
  color: ${colors.darkestGrey};
  ${fontWeight('heavy')};
  margin-bottom: 25px;
`;

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
